/* Oceanslure / Dawn helpers
   - Desktop: Thumbnail click sets Colour option (REAL change)
   - Desktop: Prevent Dawn from reordering thumbnails (no prepend)
   - Desktop: Prevent thumbnail strip snapping back to start (restore scrollLeft)
   - Desktop: Optional anti-flash during section re-render (ol-pdp-updating / ol-pdp-ready)
   - Mobile: Swipe/arrows sync Colour UI silently (no re-render / no reset)
*/
(() => {
  const norm = (s) => String(s || "").trim().toLowerCase();
  const DESKTOP = () => window.matchMedia("(min-width: 990px)").matches;
  const MOBILE = () => window.matchMedia("(max-width: 989px)").matches;

  /* ---------------- DESKTOP PATCH: stop Dawn "prepend" (thumbnail jumps to front) ---------------- */
  function preventDawnPrependOnDesktop() {
    if (window.__olNoPrependPatched) return;
    window.__olNoPrependPatched = true;

    if (!window.customElements || !customElements.whenDefined) return;

    customElements.whenDefined("media-gallery").then(() => {
      const MG = customElements.get("media-gallery");
      if (!MG || !MG.prototype) return;

      if (typeof MG.prototype.setActiveMedia !== "function") {
        console.log("⚠️ Oceanslure: media-gallery.setActiveMedia not found; no-prepend patch not applied");
        return;
      }

      const original = MG.prototype.setActiveMedia;

      MG.prototype.setActiveMedia = function (mediaId, prepend) {
        if (DESKTOP()) prepend = false;
        return original.call(this, mediaId, prepend);
      };

      console.log("✅ Oceanslure: Desktop thumbnail reorder disabled (no prepend)");
    });
  }

  /* ---------------- shared: find variant-selects ---------------- */
  function getScope(fromTarget) {
    return (
      fromTarget?.closest('section[id^="shopify-section-"]') ||
      fromTarget?.closest('[id^="MainProduct-"]') ||
      fromTarget?.closest("product-info") ||
      document
    );
  }

  function getVariantSelects(fromTarget) {
    const scope = getScope(fromTarget);
    return scope.querySelector("variant-selects") || document.querySelector("variant-selects");
  }

  function findColourSelect(variantSelects) {
    if (!variantSelects) return null;
    const selects = Array.from(variantSelects.querySelectorAll("select"));
    for (const select of selects) {
      const label = select.closest(".product-form__input")?.querySelector("label");
      const txt = norm(label?.textContent);
      if (txt.includes("colour") || txt.includes("color")) return select;
    }
    return selects[0] || null;
  }

  function findColourGroup(variantSelects) {
    if (!variantSelects) return null;
    const groups = Array.from(variantSelects.querySelectorAll(".product-form__input"));
    for (const g of groups) {
      const label = g.querySelector("label");
      const txt = norm(label?.textContent);
      if (txt.includes("colour") || txt.includes("color")) return g;
    }
    return groups[0] || null;
  }

  function matchOption(select, wantedText) {
    const wanted = norm(wantedText);
    return Array.from(select.options).find((o) => {
      const v = norm(o.value);
      return v === wanted || v.includes(wanted) || wanted.includes(v);
    });
  }

  /* ---------------- DESKTOP: preserve thumbnail strip scrollLeft through rerender ---------------- */
  function getThumbSliderHost(fromTarget) {
    const scope = getScope(fromTarget);
    return (
      scope.querySelector(".thumbnail-slider") ||
      scope.querySelector('[id^="GalleryThumbnails-"]') ||
      document.querySelector(".thumbnail-slider") ||
      document.querySelector('[id^="GalleryThumbnails-"]')
    );
  }

  function getThumbTrackFromHost(host) {
    if (!host) return null;
    return host.querySelector(".slider") || host;
  }

  function captureThumbScroll(fromTarget) {
    const host = getThumbSliderHost(fromTarget);
    const track = getThumbTrackFromHost(host);
    return {
      hostSelector: host?.classList?.contains("thumbnail-slider")
        ? ".thumbnail-slider"
        : (host?.id ? `#${host.id}` : '[id^="GalleryThumbnails-"]'),
      left: track ? track.scrollLeft : null,
    };
  }

  function restoreThumbScrollAfterRerender(fromTarget, saved) {
    if (!saved || typeof saved.left !== "number") return;

    const scope = getScope(fromTarget);
    const start = performance.now();
    const duration = 1200;

    const apply = () => {
      let host = null;
      try {
        host = scope.querySelector(saved.hostSelector);
      } catch (e) {
        host = null;
      }
      host = host || getThumbSliderHost(scope);

      const track = getThumbTrackFromHost(host);
      if (track) {
        const prev = track.style.scrollBehavior;
        track.style.scrollBehavior = "auto";
        track.scrollLeft = saved.left;
        track.style.scrollBehavior = prev || "";
      }

      if (performance.now() - start < duration) requestAnimationFrame(apply);
    };

    requestAnimationFrame(apply);
  }

  /* ---------------- DESKTOP: thumbnail click -> REAL variant change (with anti-flash) ---------------- */
  function setColourFromAlt_REAL_CHANGE(alt, fromTarget) {
    if (!alt) return false;

    const variantSelects = getVariantSelects(fromTarget);
    if (!variantSelects) return false;

    const sel = findColourSelect(variantSelects);
    if (!sel) return false;

    const opt = matchOption(sel, alt);
    if (!opt) return false;

    if (sel.value === opt.value) return true;

    // Save thumb strip position so it doesn’t snap back
    const savedScroll = captureThumbScroll(fromTarget);

    // Anti-flash (desktop only)
    if (DESKTOP()) {
      document.documentElement.classList.add("ol-pdp-updating");
      document.documentElement.classList.remove("ol-pdp-ready");
    }

    sel.value = opt.value;
    sel.dispatchEvent(new Event("change", { bubbles: true }));

    // Restore thumb strip scroll after rerender
    restoreThumbScrollAfterRerender(fromTarget, savedScroll);

    // Always release anti-flash state (safe fallback)
    if (DESKTOP()) {
      setTimeout(() => {
        document.documentElement.classList.remove("ol-pdp-updating");
        document.documentElement.classList.add("ol-pdp-ready");
      }, 260);
    }

    return true;
  }

  function onThumbnailClick(e) {
    if (!DESKTOP()) return;

    const thumb = e.target.closest("button.thumbnail");
    if (!thumb) return;

    const img = thumb.querySelector("img[alt]");
    const alt = img?.getAttribute("alt");
    if (!alt) return;

    setColourFromAlt_REAL_CHANGE(alt, e.target);
  }

  /* ---------------- MOBILE: silent sync (no rerender) ---------------- */
  function setColourSilently(fromTarget, colourText) {
    if (!colourText) return false;

    const variantSelects = getVariantSelects(fromTarget);
    if (!variantSelects) return false;

    // Prefer dropdown
    const sel = findColourSelect(variantSelects);
    if (sel) {
      const opt = matchOption(sel, colourText);
      if (!opt) return false;

      sel.value = opt.value;
      sel.dispatchEvent(new Event("input", { bubbles: false }));
      return true;
    }

    // Fallback radios
    const group = findColourGroup(variantSelects);
    const wanted = norm(colourText);
    const radios = Array.from(group?.querySelectorAll('input[type="radio"]') || []);
    for (const r of radios) {
      const v = norm(r.value || r.getAttribute("value"));
      if (v && (v === wanted || v.includes(wanted) || wanted.includes(v))) {
        r.checked = true;
        r.dispatchEvent(new Event("input", { bubbles: false }));
        return true;
      }
    }

    return false;
  }

  function getAltFromMostCenteredSlide(galleryEl) {
    const track =
      galleryEl.querySelector(".slider") ||
      galleryEl.querySelector(".product__media-list") ||
      galleryEl;

    const slides = Array.from(track.querySelectorAll("li.product__media-item"));
    if (!slides.length) return null;

    const trackRect = track.getBoundingClientRect();
    const cx = trackRect.left + trackRect.width / 2;

    let best = null;
    let bestDist = Infinity;

    for (const li of slides) {
      const r = li.getBoundingClientRect();
      if (r.right < trackRect.left - 20 || r.left > trackRect.right + 20) continue;

      const liCx = r.left + r.width / 2;
      const dist = Math.abs(liCx - cx);
      if (dist < bestDist) {
        bestDist = dist;
        best = li;
      }
    }

    const img = best?.querySelector("img[alt]");
    const alt = img?.getAttribute("alt");
    return alt && alt.trim() ? alt.trim() : null;
  }

  function attachMobileGallerySync(galleryEl) {
    if (!galleryEl || galleryEl.__olMobileSyncAttached) return;
    galleryEl.__olMobileSyncAttached = true;

    let lastAlt = null;
    let ticking = false;

    const sync = () => {
      ticking = false;
      const alt = getAltFromMostCenteredSlide(galleryEl);
      if (!alt || alt === lastAlt) return;
      lastAlt = alt;
      setColourSilently(galleryEl, alt);
    };

    const requestSync = () => {
      if (!MOBILE()) return;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sync);
    };

    const track =
      galleryEl.querySelector(".slider") ||
      galleryEl.querySelector(".product__media-list") ||
      galleryEl;

    track.addEventListener("scroll", requestSync, { passive: true });
    track.addEventListener("touchend", requestSync, true);
    track.addEventListener("pointerup", requestSync, true);

    requestSync();
    setTimeout(requestSync, 250);
    setTimeout(requestSync, 900);
  }

  /* ---------------- init ---------------- */
  function init() {
    preventDawnPrependOnDesktop();

    document.removeEventListener("click", onThumbnailClick, true);
    document.addEventListener("click", onThumbnailClick, true);

    document.querySelectorAll('[id^="MediaGallery-"]').forEach(attachMobileGallerySync);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
  setTimeout(init, 800);
})();